import HighchartsReact from "highcharts-react-official";
import {useMemo} from "react";

import Highcharts from "@/components/Highcharts";
import {MetricSeries, MetricSeriesAnalysis} from "@/lib/types/metricsExplorer.ts";
import {ChartEmptyState} from "@/pages/metricsExplorer/charts/ChartEmptyState.tsx";
import {formatMetricValue} from "@/pages/metricsExplorer/chartSeries.ts";
import {buildHeatmapPoints, pickSeasonality, WEEKDAY_LABELS} from "@/pages/metricsExplorer/chartShapes.ts";

import chartStyles from "./Chart.module.scss";

interface SeasonalityHeatmapProps {
  series: MetricSeries[];
  analysis: MetricSeriesAnalysis[];
}

const HOURS = Array.from({length: 24}, (_, hour) => `${String(hour).padStart(2, "0")}`);

/**
 * Average value by weekday and hour.
 *
 * The grid comes from the analysis engine rather than being bucketed here, so the heatmap
 * and any alert on seasonality are reading the same numbers — including the timezone the
 * hours were bucketed in, which UTC-stored timestamps make easy to get wrong.
 */
export const SeasonalityHeatmap = ({series, analysis}: SeasonalityHeatmapProps) => {
  const chosen = useMemo(() => pickSeasonality(analysis), [analysis]);
  const source = useMemo(() => series.find((item) => item.key === chosen?.series_key), [series, chosen]);

  const options = useMemo(() => {
    if (!chosen?.seasonality) {
      return null;
    }
    const unit = source?.unit ?? null;
    return {
      chart: {type: "heatmap", marginTop: 40, marginBottom: 60},
      title: {text: ""},
      credits: {enabled: false},
      xAxis: {categories: HOURS, title: {text: `Hour of day (${chosen.seasonality.timezone})`}},
      yAxis: {categories: WEEKDAY_LABELS, title: {text: ""}, reversed: true},
      colorAxis: {minColor: "#EAF4EC", maxColor: "#1F7A3D"},
      legend: {align: "right", layout: "vertical", verticalAlign: "middle", symbolHeight: 200},
      tooltip: {
        outside: true,
        formatter: function (this: {point: {x: number; y: number; value: number | null}}): string {
          const {x, y, value} = this.point;
          return `<b>${WEEKDAY_LABELS[y]} ${HOURS[x]}:00</b><br/>${formatMetricValue(value, unit)}`;
        },
      },
      series: [
        {
          name: source?.label ?? "Value",
          borderWidth: 1,
          borderColor: "#FFFFFF",
          data: buildHeatmapPoints(chosen.seasonality.hour_by_weekday),
          // Slots the window never covered are absent data, not a zero reading.
          nullColor: "#F5F5F5",
        },
      ],
    };
  }, [chosen, source]);

  if (!chosen?.seasonality) {
    return (
      <ChartEmptyState
        title="Seasonality not computed"
        message="Turn on Seasonality in the analysis panel to see the weekday and hour profile."
      />
    );
  }

  return (
    <HighchartsReact highcharts={Highcharts} options={options} containerProps={{className: chartStyles.container}} />
  );
};
